/**
 * compareTo checks for pasted AI rule JSON — the RHS metric must exist, the
 * percent must be sane, its window must be a known preset (checkWindowIssues
 * reused), and a self-comparison at 100% over the same window is flagged as
 * the tautology it is.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { RuleCompareTo } from '@trafficpulse/shared';

import type { LibraryValidationContext, ReviewIssue } from '../review-items';
import { checkWindowIssues } from './date-window';

export function checkCompareToIssues(
  lhsMetric: string,
  ct: RuleCompareTo,
  ctx: LibraryValidationContext,
  issues: ReviewIssue[],
): void {
  if (typeof ct.metric !== 'string' || !ct.metric) {
    issues.push({ level: 'error', message: 'compareTo needs a "metric".' });
  } else if (ctx.metricIds?.size && !ctx.metricIds.has(ct.metric)) {
    issues.push({
      level: 'error',
      message: `Unknown metric "${ct.metric}" in a compareTo.`,
    });
  }
  if (
    ct.against !== undefined &&
    ct.against !== 'self' &&
    ct.against !== 'total'
  ) {
    issues.push({
      level: 'error',
      message: `compareTo "against" must be "self" or "total" (got "${String(ct.against)}").`,
    });
  }
  const p = ct.percent;
  if (p !== undefined && (typeof p !== 'number' || !Number.isFinite(p) || p <= 0)) {
    issues.push({
      level: 'error',
      message: 'compareTo percent must be a number above 0 (or omitted).',
    });
  }
  checkWindowIssues(
    {
      dateRange: ct.dateRange,
      customFrom: ct.dateRangeCustomFrom,
      customTo: ct.dateRangeCustomTo,
    },
    ctx,
    issues,
  );
  if (!ct.dateRange && ct.metric === lhsMetric && (p ?? 100) === 100) {
    issues.push({
      level: 'warning',
      message: `Comparing ${lhsMetric} against 100% of itself over the same window never changes anything.`,
    });
  }
}
